import {
  Control,
  FieldErrors,
  UseFormClearErrors,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form"
import {
  IProductPrice,
  IProductPricePaymentInstallment,
} from "../../../../core/types/Product"
import { TSale } from "../../../../core/types/Sale"
import React from "react"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Button,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { TCheckoutLayout } from "../../../../core/types/Checkout"
import PaymentsByProduct4 from "./Payment"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import LocalOfferIcon from "@mui/icons-material/LocalOffer"
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import { TProductVoucherValue } from "../../../../core/types/Voucher"
import NumberFunctions from "../../../../core/functions/NumberFunctions"
import { useCheckoutContext } from "../../../../core/hooks/useCheckoutContext"

export interface ICheckoutPayments5 {
  layout?: TCheckoutLayout
  productPriceId: string
  loading: boolean
  loadingVoucher: boolean
  data: IProductPrice | null
  control: Control<TSale, any>
  watch: UseFormWatch<TSale>
  setValue: UseFormSetValue<TSale>
  errors: FieldErrors<TSale>
  installments?: IProductPricePaymentInstallment[]
  clearErrors: UseFormClearErrors<TSale>
  onApplyVoucher: () => void
  onRemoveVoucher: () => void
}

const CheckoutPayments5 = ({
  layout,
  productPriceId,
  loading,
  loadingVoucher,
  data,
  control,
  watch,
  setValue,
  errors,
  installments,
  clearErrors,
  onApplyVoucher,
  onRemoveVoucher,
}: ICheckoutPayments5) => {
  const { displayCurrency } = useCheckoutContext()
  const voucherIdentifier = watch("payment.voucherIdentifier")
  const voucherStatus = watch("payment.voucherStatus")
  const voucherType = watch("payment.voucherType")
  const voucherValue = watch("payment.voucherValue")

  return (
    <Box sx={{ pt: 1 }}>
      {data?.hasVoucher && (
        <Box sx={{ mb: 3, border: "1px solid #DCDDDE", borderRadius: "4px" }}>
          <Accordion elevation={0} data-cy="panelVoucherContent">
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel-voucher-content"
              id="panel-voucher-header"
            >
              {["none", "invalid"].includes(voucherStatus) && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <LocalOfferIcon
                    sx={{
                      color: "white",
                      stroke: "#323232",
                      strokeWidth: 1.5,
                      fontSize: "1.3rem",
                    }}
                  />
                  <Typography fontSize="14px" lineHeight="18px">
                    Tem um cupom de desconto?
                  </Typography>
                </Stack>
              )}
              {["valid"].includes(voucherStatus) && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CheckCircleOutlineIcon
                    color="primary"
                    sx={{ fontSize: "1.3rem" }}
                  />
                  <Typography fontSize="14px" lineHeight="18px">
                    Cupom <strong>{voucherIdentifier}</strong> aplicado!
                  </Typography>
                </Stack>
              )}
            </AccordionSummary>
            <AccordionDetails>
              <Stack direction="row" spacing={2} alignItems="start">
                <TextField
                  fullWidth
                  size="small"
                  disabled={loadingVoucher || voucherStatus === "valid"}
                  {...control.register("payment.voucherIdentifier")}
                  error={voucherStatus === "invalid"}
                  helperText={
                    voucherStatus === "valid"
                      ? `Você recebeu um cupom de ${
                          voucherType === TProductVoucherValue.value
                            ? NumberFunctions.formatMoneyCurrency(voucherValue, displayCurrency)
                            : NumberFunctions.toPercentage(voucherValue)
                        }`
                      : ""
                  }
                  id="voucherIdentifier"
                />
                {["none", "invalid"].includes(voucherStatus) && (
                  <Button
                    id="buttonVoucherApply"
                    variant="contained"
                    size="large"
                    sx={{ textTransform: "none" }}
                    onClick={onApplyVoucher}
                    disabled={
                      loadingVoucher || (voucherIdentifier ?? "") === ""
                    }
                  >
                    Aplicar
                  </Button>
                )}
                {["valid"].includes(voucherStatus) && (
                  <Button
                    id="buttonVoucherRemove"
                    variant="outlined"
                    color="error"
                    size="large"
                    sx={{ textTransform: "none" }}
                    onClick={onRemoveVoucher}
                    disabled={loadingVoucher}
                  >
                    Excluir
                  </Button>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}

      <Stack direction="row" spacing={1} alignItems="center" sx={{ pb: 1.5 }}>
        <Box
          sx={{
            backgroundColor: "rgba(8, 190, 75, 0.08)",
            width: 334,
            height: 40,
            borderRadius: 40,
            padding: "0px 16px 0px 0px",
            gap: 8,
          }}
        >
          <Stack direction={"row"} sx={{ alignItems: "center" }} spacing={1}>
            <Avatar sx={{ bgcolor: "rgba(8, 190, 75, 1)" }}>
              <Typography
                fontSize="16px"
                lineHeight="20px"
                fontWeight={700}
                color="rgba(255, 255, 255, 1)"
              >
                2
              </Typography>
            </Avatar>
            <Typography
              fontSize="16px"
              lineHeight="20px"
              fontWeight={700}
              color="rgba(52, 57, 72, 1)"
              sx={{ textTransform: "uppercase" }}
            >
              Informações de Pagamento
            </Typography>
          </Stack>
        </Box>
      </Stack>
      <PaymentsByProduct4
        layout={layout}
        productPriceId={productPriceId}
        data={data}
        loading={loading}
        control={control}
        setValue={setValue}
        watch={watch}
        errors={errors}
        installments={installments}
        clearErrors={clearErrors}
      />
    </Box>
  )
}

export default CheckoutPayments5
